import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const KEY = "mr_cookie_consent";

const Toggle = ({ on, onChange, disabled = false, testId }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!on)}
    data-testid={testId}
    aria-pressed={on}
    className={`w-11 h-6 rounded-full relative transition-colors shrink-0 ${
      on ? "bg-cyan-400" : "bg-zinc-300"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
        on ? "left-[1.375rem]" : "left-0.5"
      }`}
    />
  </button>
);

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [prefs, setPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const openPrefs = () => {
      setShow(true);
      setPrefs(true);
    };
    window.addEventListener("mr-open-cookie-prefs", openPrefs);
    return () => window.removeEventListener("mr-open-cookie-prefs", openPrefs);
  }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("cookies") === "demo") {
      const t = setTimeout(() => setShow(true), 1000);
      return () => clearTimeout(t);
    }
    if (localStorage.getItem(KEY)) return;
    const ctrl = new AbortController();
    const kill = setTimeout(() => ctrl.abort(), 5000);
    (async () => {
      try {
        const res = await fetch("https://ipwho.is/", { signal: ctrl.signal });
        const data = await res.json();
        const eea =
          data && (data.is_eu || ["GB", "NO", "IS", "LI", "CH"].includes(data.country_code));
        if (eea) {
          setTimeout(() => setShow(true), 1200);
        }
      } catch {
        // lookup failed — show the notice to stay compliant
        setTimeout(() => setShow(true), 1500);
      }
    })();
    return () => {
      ctrl.abort();
      clearTimeout(kill);
    };
  }, []);

  const save = (choice) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        necessary: true,
        analytics: choice === "all" ? true : choice === "none" ? false : analytics,
        marketing: choice === "all" ? true : choice === "none" ? false : marketing,
        choice,
        ts: new Date().toISOString(),
      })
    );
    setShow(false);
    setPrefs(false);
  };

  return (
    <AnimatePresence>
      {show && !prefs && (
        <motion.div
          key="banner"
          data-testid="cookie-banner"
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-[100] bottom-4 left-4 right-4 sm:right-auto sm:left-6 sm:bottom-6 sm:max-w-md bg-white text-black border border-zinc-200 rounded-2xl shadow-2xl p-6"
          role="dialog"
          aria-label="Cookie consent"
        >
          <h2 className="font-display text-xl tracking-tight">A word on cookies</h2>
          <p className="text-sm text-zinc-600 leading-relaxed mt-2">
            We use cookies to run the site and, with your permission, to understand how
            it's used and improve it. You can accept, reject or choose your own selection.{" "}
            <Link
              to="/cookie-policy"
              data-testid="cookie-policy-link"
              className="text-cyan-600 underline decoration-cyan-400/40 underline-offset-4 hover:decoration-cyan-500"
            >
              Read our cookie policy
            </Link>
            .
          </p>
          <div className="flex flex-wrap items-center gap-2.5 mt-5">
            <button
              onClick={() => save("all")}
              data-testid="cookie-accept-all"
              className="rounded-full bg-black text-white text-sm font-semibold px-5 py-2.5 hover:bg-cyan-400 hover:text-black transition-colors"
            >
              Accept all
            </button>
            <button
              onClick={() => save("none")}
              data-testid="cookie-reject-all"
              className="rounded-full border border-zinc-300 text-sm font-semibold px-5 py-2.5 hover:border-cyan-500 hover:text-cyan-600 transition-colors"
            >
              Reject all
            </button>
            <button
              onClick={() => setPrefs(true)}
              data-testid="cookie-customise"
              className="text-sm font-medium text-zinc-600 underline underline-offset-4 hover:text-cyan-600 transition-colors px-2 py-2.5"
            >
              Customise
            </button>
          </div>
        </motion.div>
      )}

      {show && prefs && (
        <motion.div
          key="prefs"
          data-testid="cookie-preferences-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white text-black rounded-3xl max-w-lg w-full p-7 sm:p-9"
            role="dialog"
            aria-label="Cookie preferences"
          >
            <div className="flex items-start justify-between gap-6">
              <h2 className="font-display text-2xl tracking-tight">Cookie preferences</h2>
              <button
                onClick={() => setPrefs(false)}
                data-testid="cookie-preferences-close"
                aria-label="Back"
                className="w-9 h-9 grid place-items-center rounded-full border border-zinc-200 hover:border-cyan-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed mt-2">
              Choose which cookies you're happy with. Necessary cookies keep the site
              working and can't be switched off.
            </p>

            <div className="space-y-4 mt-7">
              <div className="flex items-start justify-between gap-6 border border-zinc-200 rounded-2xl p-5">
                <div>
                  <h3 className="font-semibold text-sm">Strictly necessary</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed mt-1">
                    Required for security, consent preferences and core site function.
                  </p>
                </div>
                <Toggle on={true} onChange={() => {}} disabled testId="cookie-toggle-necessary" />
              </div>
              <div className="flex items-start justify-between gap-6 border border-zinc-200 rounded-2xl p-5">
                <div>
                  <h3 className="font-semibold text-sm">Analytics</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed mt-1">
                    Anonymous usage statistics that help us improve the site.
                  </p>
                </div>
                <Toggle on={analytics} onChange={setAnalytics} testId="cookie-toggle-analytics" />
              </div>
              <div className="flex items-start justify-between gap-6 border border-zinc-200 rounded-2xl p-5">
                <div>
                  <h3 className="font-semibold text-sm">Marketing</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed mt-1">
                    Used to measure campaigns and show relevant content.
                  </p>
                </div>
                <Toggle on={marketing} onChange={setMarketing} testId="cookie-toggle-marketing" />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2.5 mt-7">
              <button
                onClick={() => save("custom")}
                data-testid="cookie-save-preferences"
                className="rounded-full bg-black text-white text-sm font-semibold px-5 py-2.5 hover:bg-cyan-400 hover:text-black transition-colors"
              >
                Save my selection
              </button>
              <button
                onClick={() => save("all")}
                data-testid="cookie-preferences-accept-all"
                className="rounded-full border border-zinc-300 text-sm font-semibold px-5 py-2.5 hover:border-cyan-500 hover:text-cyan-600 transition-colors"
              >
                Accept all
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-5">
              Details in our{" "}
              <Link
                to="/cookie-policy"
                className="text-cyan-600 underline decoration-cyan-400/40 underline-offset-4 hover:decoration-cyan-500"
              >
                cookie policy
              </Link>
              .
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
